import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';
import {breakpoints, configType} from 'UI/utils/globalStyles';

import type {Config, SpacerObject} from '@nfq/react-grid';
import type {Media} from 'types/payload-types';

interface Source {
    media?: string;
    sizes?: string;
    srcSet: string;
    type: string;
}

/**
 * A custom React hook that generates responsive image metadata from a Payload CMS media object.
 * This hook prepares all necessary attributes for rendering an optimized `<picture>` element including `src`, `srcSet`,
 * fallback `srcSet`, `alt` text, and responsive `sizes`. It handles both populated and string reference media inputs safely
 * and falls back to empty values when no valid image is provided. If `expectedSizes` are given, the hook generates a `sizes` string
 * using the configured breakpoints to support responsive image loading.
 *
 * @param image         A `Media` object, string ID, or `null`/`undefined` representing the image to be rendered.
 * @param expectedSizes An optional object specifying how wide the image should appear at different breakpoints (e.g., `{ md: 400 }`).
 * @returns An object containing `alt`, `src`, `srcSet`, `srcSetFallback`, and `sizes` attributes for use in a `<picture>` or `<img>` tag.
 *
 * @example
 * ```tsx
 * const { src, srcSet, srcSetFallback, alt, sizes } = usePayloadImage(image, { md: 600, lg: 800 });
 * return (
 *   <picture>
 *     <source type="image/webp" srcSet={srcSet} sizes={sizes} />
 *     <source type="image/png" srcSet={srcSetFallback} sizes={sizes} />
 *     <img src={src} alt={alt} sizes={sizes} />
 *   </picture>
 * );
 * ```
 */
export const usePayloadImage = (image: Media | string | null | undefined, expectedSizes?: SpacerObject) => {
    const configBreakpoints = (configType as Config<'light'>).breakpoints;
    let sources: Source[] = [];

    let alt = '';
    let src = null;
    let width: number | null | undefined;
    let height: number | null | undefined;

    if (isDefinedAndPopulated(image)) {
        width = image.width;
        height = image.height;

        if (isDefined(image.sizes) && expectedSizes) {
            const sizes = breakpoints.map(breakpoint => {
            // eslint-disable-next-line security/detect-object-injection
                if (expectedSizes[breakpoint]) {
                    // eslint-disable-next-line security/detect-object-injection
                    return `(min-width: ${configBreakpoints?.[breakpoint]}px) ${expectedSizes[breakpoint]}px`;
                }

                return null;
            }).filter(Boolean).reverse().join(', ');
            const srcFiles = Object.entries(image.sizes).map(([key, value]) => {
                if (!key.includes('fallback') && value.url && value.width && value.mimeType) {
                    return {
                        srcSet: `${value.url} ${value.width}w`,
                        type: value.mimeType,
                        width: value.width
                    };
                }

                return null;
            }).filter(Boolean);

            if (srcFiles.length > 0) {
                sources.push({
                    sizes,
                    srcSet: srcFiles.map(file => file.srcSet).join(', '),
                    type: srcFiles[0].type
                });
            }

            const fallbackSrcFiles = Object.entries(image.sizes).map(([key, value]) => {
                if (key.includes('fallback') && value.url && value.width && value.mimeType) {
                    return {
                        srcSet: `${value.url} ${value.width}w`,
                        type: value.mimeType
                    };
                }

                return null;
            }).filter(Boolean);

            if (fallbackSrcFiles.length > 0) {
                sources.push({
                    sizes,
                    srcSet: srcFiles.map(file => file.srcSet).join(', '),
                    type: srcFiles[0].type
                });
            }
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            alt = image.alt ?? '';
            src = image.url ?? null;
        } else if (isDefined(image.sizes) && !expectedSizes) {
            sources = Object.entries(image.sizes).map(([key, value]) => {
                const breakpointKey = key.replace('fallback-', '');
                const nextBreakpoint = breakpoints[
                    breakpoints.indexOf(breakpointKey as typeof breakpoints[number]) + 1
                ];

                if (value.url && value.width && value.mimeType) {
                    return {
                        // eslint-disable-next-line security/detect-object-injection
                        media: `(max-width: ${(configBreakpoints?.[nextBreakpoint] ?? 1) - 1}px)`,
                        srcSet: value.url,
                        type: value.mimeType
                    };
                }

                return null;
            }).filter(Boolean);

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            alt = image.alt ?? '';
            src = image.url ?? null;
        }
    }

    return {
        alt,
        height,
        sources,
        src,
        width
    };
};